import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import type { LeadDetail, LeadRow } from '../../lib/leads';
import { Button, Modal } from '../ui';
import { LeadPicker } from './LeadPicker';

/** Two records for the same person → one. This lead is kept and gets everything from the other. */
export function MergeDialog({ lead, onClose, onMerged }: { lead: LeadDetail; onClose: () => void; onMerged: () => void }) {
  const qc = useQueryClient();
  const [other, setOther] = useState<LeadRow | null>(null);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    if (!other) return;
    setBusy(true);
    try {
      const r = await api.post<{ message: string }>(`/leads/${lead.id}/merge`, { otherId: other.id });
      toast.success(r.message);
      await qc.invalidateQueries({ queryKey: ['leads'] });
      onMerged();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Couldn’t merge. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`Merge into L-${lead.number} ${lead.name}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!other} loading={busy} onClick={merge}>Merge</Button></>}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Pick the other record of the same person. Its history, calls, WhatsApp chats, quotations, tasks and tags move here, and any details this lead is missing
          (email, company, second phone…) are copied over. The other record then goes to Trash.
        </p>
        <LeadPicker value={other} onChange={setOther} exclude={lead.id} placeholder="Search the duplicate by name or phone" />
        {other && <p className="rounded-lg bg-amber-50 p-3 text-sm dark:bg-amber-950/40">L-{other.number} {other.name} will be merged into L-{lead.number} {lead.name}. This can’t be undone.</p>}
      </div>
    </Modal>
  );
}
